import { useState } from "react";
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useQuoteActions } from "../hooks/useQuoteActions";
import { useDialog } from "../../ui/dialogs/useDialog";

// BaseModalComponent stacks modals at ~15000+index; Menu/Popover defaults to theme zIndex.modal (~1300).
const MENU_Z_INDEX_ABOVE_MODALS = 20000;

/**
 * Kebab menu rendered inside a QuoteRow. Exposes archive / unarchive / delete, guarding the
 * destructive ones with a confirmation dialog. Clicks are contained with stopPropagation so
 * the row's own modal trigger doesn't fire.
 */
export function QuoteRowActionsMenu({ quote }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const { archive, unarchive, remove, isBusy } = useQuoteActions(quote.id);
  const { showDialog, DialogComponent } = useDialog();

  const isArchived = Boolean(quote.archivedAt);
  const hasPaidDeposit = Boolean(quote.depositInvoice?.paidAt && !quote.depositInvoice?.refundedAt);

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const stop = (event) => event.stopPropagation();

  const handleArchive = (event) => {
    stop(event);
    setAnchorEl(null);
    showDialog({
      type: "warning",
      title: "Archive Quote",
      message: `Archive quote #${quote.id}? It stays in your history and can be restored later.`,
      confirmLabel: "Archive",
      cancelLabel: "Cancel",
      onConfirm: () => archive(),
      onCancel: () => undefined,
    });
  };

  const handleUnarchive = (event) => {
    stop(event);
    setAnchorEl(null);
    unarchive();
  };

  const handleDelete = (event) => {
    stop(event);
    setAnchorEl(null);
    if (hasPaidDeposit) {
      showDialog({
        type: "warning",
        title: "Cannot Delete",
        message: "This quote has a paid deposit. Archive it instead to keep the payment history intact.",
        confirmLabel: "Archive Instead",
        cancelLabel: "Cancel",
        onConfirm: () => archive(),
        onCancel: () => undefined,
      });
      return;
    }
    showDialog({
      type: "warning",
      title: "Delete Quote",
      message: `Delete quote #${quote.id} permanently? This cannot be undone.`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      onConfirm: () => remove(),
      onCancel: () => undefined,
    });
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen} disabled={isBusy} aria-label="Quote actions">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={stop}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1.5,
            width: 260,
            borderRadius: 2,
            zIndex: 99999,
          },
        }}
        sx={{ zIndex: 99999 }}
      >
        {isArchived ? (
          <MenuItem onClick={handleUnarchive}>
            <ListItemIcon>
              <UnarchiveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Unarchive" />
          </MenuItem>
        ) : (
          <MenuItem onClick={handleArchive}>
            <ListItemIcon>
              <ArchiveIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Archive" />
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>
      {DialogComponent}
    </>
  );
}
